import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { getOrgTokens, ORG_PALETTE } from '../theme/style/organizationPalette';

const MetricCard = ({
    title,
    subtitle,
    value,
    unit,
    icon: Icon,
    accent,
    bg,
    type, // 'chain', 'scope1', 'scope2', 'scope3', 'gwp', 'cost', 'shield'
    delay = 0,
    variant = 'default', // 'default' or 'stage'
    forceTons = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const typeTokens = (isDark && type) ? ORG_PALETTE.metricCards[type] : null;

    // Helper for value conversion + smart unit display
    const { displayValue, displayUnit } = useMemo(() => {
        if (typeof value !== 'number') return { displayValue: value, displayUnit: unit };

        // Handle circularity index
        if (unit?.includes('Index')) return { displayValue: value.toFixed(2), displayUnit: unit };

        // Smart kg ↔ tonne conversion for CO₂ units
        if (unit?.includes('CO')) {
            if (value >= 1000 || forceTons) {
                // show in tonnes
                const valInTons = value / 1000;
                let decimals = 2;
                if (valInTons > 0 && valInTons < 0.1) {
                    decimals = 3;
                }
                return {
                    displayValue: valInTons.toLocaleString(undefined, {
                        minimumFractionDigits: valInTons === 0 ? 1 : 2,
                        maximumFractionDigits: decimals
                    }),
                    displayUnit: 't CO₂e'
                };
            } else {
                // show in kg
                return {
                    displayValue: value.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                    }),
                    displayUnit: 'kg CO₂e'
                };
            }
        }

        // Preserve decimals for percentage values
        if (unit === '%') {
            return {
                displayValue: value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                displayUnit: unit
            };
        }

        return {
            displayValue: value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            displayUnit: unit
        };
    }, [value, unit, forceTons]);

    const isStage = variant === 'stage';
    const isCompact = variant === 'compact';

    return (
        <Box
            className={`eng-metric-card eng-metric-card--${variant}`}
            sx={{
                animationDelay: `${delay}s`,
                background: bg || tok.bg.metricDefault,
                p: isStage ? '10px 14px' : (isCompact ? '14px 18px' : '24px'),
                borderRadius: tok.radius.card,
                border: '1px solid',
                borderColor: typeTokens?.borderDark || tok.border.card,
                minHeight: isStage ? '76px' : (isCompact ? '84px' : '150px'),
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: isStage ? '12px' : (isCompact ? '14px' : '20px'),
                boxShadow: tok.shadow.card,
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: ORG_PALETTE.animation.cardHover,
                    boxShadow: tok.shadow.cardHover
                }
            }}>

            {/* Icon Column */}
            {Icon && (
                <Box className="eng-metric-card__icon-box" sx={{
                    background: typeTokens?.iconBgDark || (isDark ? 'rgba(255,255,255,0.08)' : '#fff'),
                    width: isStage ? 36 : (isCompact ? 40 : 56),
                    height: isStage ? 36 : (isCompact ? 40 : 56),
                    borderRadius: isStage ? ORG_PALETTE.radius.iconSm : (isCompact ? '10px' : ORG_PALETTE.radius.icon),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: tok.shadow.iconBox,
                    flexShrink: 0
                }}>
                    <Icon size={isStage ? 18 : (isCompact ? 20 : 28)} color={(isDark && typeTokens?.iconColorDark) ? typeTokens.iconColorDark : accent} />
                </Box>
            )}

            {/* Content Column: Value and Title */}
            <Box sx={{
                display: 'flex',
                flexDirection: isStage ? 'row' : 'column',
                alignItems: isStage ? 'center' : 'flex-start',
                justifyContent: isStage ? 'space-between' : 'flex-start',
                flexGrow: 1,
                minWidth: 0,
                gap: isStage ? '8px' : (isCompact ? '2px' : '6px')
            }}>
                <Typography className="eng-metric-card__value" sx={{
                    fontSize: isStage ? '18px !important' : (isCompact ? '22px !important' : '32px !important'),
                    fontWeight: '700 !important',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px',
                    lineHeight: 1.15,
                    color: isDark ? '#ffffff' : '#1e293b',
                    letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    width: '100%'
                }}>
                    {displayValue}
                    <Typography component="span" sx={{
                        fontSize: isStage ? 10 : (isCompact ? 11.5 : 13),
                        fontWeight: '500 !important',
                        color: isDark ? tok.text.muted : '#334155',
                        mb: 0.2
                    }}>
                        {displayUnit || ''}
                    </Typography>
                </Typography>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isStage ? 'flex-end' : 'flex-start',
                    textAlign: isStage ? 'right' : 'left',
                    minWidth: 0,
                    width: '100%'
                }}>
                    <Typography className="eng-metric-card__title" sx={{
                        fontSize: isStage ? '12px !important' : (isCompact ? '13px !important' : '15px !important'),
                        fontWeight: '600 !important',
                        color: isDark ? 'rgba(255,255,255,1)' : '#0f172a',
                        fontFamily: 'Poppins, sans-serif',
                        lineHeight: 1.25,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        textAlign: isStage ? 'right' : 'left',
                        width: '100%'
                    }}>
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography sx={{
                            fontSize: isStage ? '9.5px' : (isCompact ? '10px' : '11px'),
                            fontWeight: '500 !important',
                            color: isDark ? 'rgba(255,255,255,0.7)' : '#64748b',
                            mt: 0.15,
                            textAlign: isStage ? 'right' : 'left',
                            width: '100%'
                        }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default MetricCard;
